import React, { useEffect, useState } from 'react';
import api from '../api/api';
import Icon from '../components/Icon';
import { supabase } from '../supabase';
import { useBilling } from './BillingContext';
import { formatLongDate } from './format';
import { markLeaving } from '../auth/leaving';

// Everything this app keeps in the browser: local storage, plus the whiteboard,
// which tldraw keeps in IndexedDB under its persistence key
const clearBrowserData = async () => {
    try {
        localStorage.clear();
        sessionStorage.clear();
    } catch (e) {
        // Storage unavailable; nothing to clear
    }
    try {
        const databases = (await indexedDB.databases?.()) || [];
        databases
            .filter(db => db.name?.includes('light-speed-whiteboard'))
            .forEach(db => indexedDB.deleteDatabase(db.name));
    } catch (e) {
        // Not supported here; the whiteboard copy stays on this device only
    }
};

const DeleteAccountModal = ({ onClose }) => {
    const { account, pricing } = useBilling();
    const [typed, setTyped] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const handleKey = (event) => event.key === 'Escape' && !isDeleting && onClose();
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isDeleting, onClose]);

    const planName = pricing?.plans.find(p => p.id === account?.subscribedPlan)?.name;
    const periodEnd = account?.status === 'trialing' ? account?.trialEnd : account?.currentPeriodEnd;
    const canDelete = typed.trim() === 'DELETE' && !isDeleting;

    const handleDelete = async () => {
        setIsDeleting(true);
        setError('');
        try {
            await api.deleteAccount();
            // The session belongs to a user that no longer exists; signing out may fail, which is fine
            markLeaving();
            await supabase.auth.signOut().catch(() => {});
            await clearBrowserData();
            window.location.href = '/?accountDeleted=1';
        } catch (e) {
            setError(e.response?.data?.error || "We couldn't delete your account. Please try again.");
            setIsDeleting(false);
        }
    };

    return (
        <div className="ls-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !isDeleting && onClose()}>
            <div className="ls-modal ls-cancel" role="alertdialog" aria-modal="true" aria-labelledby="delete-title" style={{ width: 'min(520px, 100%)' }}>
                <h2 id="delete-title" className="ls-modal-title">Delete your account?</h2>
                <ul className="ls-cancel-list">
                    <li><Icon name="inventory_2" size={18} />Your products, customer profiles, brand voice, plans and memory are deleted.</li>
                    <li><Icon name="link_off" size={18} />Your tracked links stop working and their click history is deleted.</li>
                    {planName && (
                        <li>
                            <Icon name="credit_card_off" size={18} />
                            <span>
                                Your {planName} plan is cancelled right away, with no refund for the rest of the period.
                                {periodEnd && ` If you only want to stop paying, cancel your plan instead and keep it until ${formatLongDate(periodEnd)}.`}
                            </span>
                        </li>
                    )}
                    <li><Icon name="receipt_long" size={18} />Invoices for past payments are kept by Stripe, as tax law requires.</li>
                </ul>
                <p className="ls-muted" style={{ margin: 0, fontSize: 14 }}>This can't be undone. Type <strong>DELETE</strong> to confirm.</p>
                <input
                    className="ls-input"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder="DELETE"
                    aria-label="Type DELETE to confirm"
                    autoComplete="off"
                    autoFocus
                    disabled={isDeleting}
                />
                {error && <div className="ls-error" role="alert"><Icon name="error" />{error}</div>}
                <div className="ls-modal-actions">
                    <button type="button" className="ls-btn ls-btn-secondary" disabled={isDeleting} onClick={onClose}>Keep my account</button>
                    <button type="button" className="ls-btn ls-btn-danger" disabled={!canDelete} onClick={handleDelete}>
                        {isDeleting ? <span className="ls-spinner" /> : 'Delete account'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// The last card on the billing page
const DeleteAccount = () => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="ls-card ls-danger-card">
            <div>
                <div className="ls-card-title">Delete account</div>
                <div className="ls-card-sub" style={{ marginTop: 2 }}>
                    Permanently delete your account and everything in it. Any paid plan is cancelled.
                </div>
            </div>
            <button type="button" className="ls-btn ls-btn-danger-outline" onClick={() => setIsOpen(true)}>
                Delete account
            </button>
            {isOpen && <DeleteAccountModal onClose={() => setIsOpen(false)} />}
        </div>
    );
};

export default DeleteAccount;
